import { useState, useEffect } from 'react';

/**
 * Version Check Hook
 * Periodically checks if a new version of the app is available
 * by comparing build timestamps in the meta tag vs localStorage
 */
export function useVersionCheck(checkInterval = 5 * 60 * 1000) { // 5 minutes default
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);

  // Get version from meta tag injected during build
  const getLatestVersion = () => {
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag?.content || null;
  };

  // Get stored version from localStorage
  const getStoredVersion = () => {
    try {
      return localStorage.getItem('app-version');
    } catch (error) {
      console.warn('localStorage unavailable:', error);
      return null;
    }
  };

  // Store current version
  const storeVersion = (version) => {
    if (version) {
      try {
        localStorage.setItem('app-version', version);
      } catch (error) {
        console.warn('Failed to store version:', error);
      }
    }
  };

  // Check for updates
  const checkForUpdates = async () => {
    try {
      // Force fetch index.html to check for new version (bypass cache)
      const response = await fetch('/', {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('Failed to check for updates:', response.status);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const metaTag = doc.querySelector('meta[name="app-version"]');
      const fetchedVersion = metaTag?.content;

      if (!fetchedVersion) {
        console.warn('No version found in fetched HTML');
        return;
      }

      setLatestVersion(fetchedVersion);

      const stored = getStoredVersion();
      setCurrentVersion(stored);

      // If versions differ, update is available
      if (stored && stored !== fetchedVersion) {
        console.log('🆕 New version available:', fetchedVersion, '(current:', stored, ')');
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.warn('Error checking for updates:', error);
    }
  };

  // Refresh the page to load new version
  const refreshApp = () => {
    // SAFETY: Prevent reload loops
    const reloadKey = 'swan-last-version-reload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    if (lastReload && (now - parseInt(lastReload)) < 10000) {
      console.error('⚠️ Version reload loop detected - aborting. Please manually refresh.');
      return;
    }

    // Warn if forms have unsaved data
    const hasForms = document.querySelectorAll('form').length > 0;
    if (hasForms) {
      const inputs = document.querySelectorAll('input:not([type="hidden"]), textarea');
      const hasUnsavedData = Array.from(inputs).some(input =>
        input.value && input.value.trim().length > 0
      );

      if (hasUnsavedData) {
        const confirmed = window.confirm(
          'You may have unsaved changes. Refresh anyway to get the latest version?'
        );
        if (!confirmed) return;
      }
    }

    sessionStorage.setItem(reloadKey, now.toString());

    // Store new version first
    const latest = getLatestVersion();
    if (latest) {
      storeVersion(latest);
    }

    // Clear service worker caches
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }

    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Force reload bypassing cache
    window.location.reload();
  };

  // Initialize: store version on first load
  useEffect(() => {
    const version = getLatestVersion();
    const stored = getStoredVersion();

    setCurrentVersion(stored);
    setLatestVersion(version);

    // If no stored version (first visit), store current
    if (!stored && version) {
      storeVersion(version);
      setCurrentVersion(version);
    }

    // If versions differ on load, update is available
    if (stored && version && stored !== version) {
      setUpdateAvailable(true);
    }
  }, []);

  // Set up periodic checking
  useEffect(() => {
    if (!checkInterval) return;

    const interval = setInterval(() => {
      checkForUpdates();
    }, checkInterval);

    return () => clearInterval(interval);
  }, [checkInterval]);

  return {
    updateAvailable,
    currentVersion,
    latestVersion,
    refreshApp,
    checkForUpdates
  };
}
